/**
 * Notes service — private, per-user notes optionally linked to a
 * transaction, property, document, party, or conversation. Backed by
 * rialty-user-service's NoteController (/api/v1/users/me/notes) — every
 * note is scoped to the caller; there is no way to read another user's
 * notes. Mirrors contact.service.js's shape.
 */
import { apiClient, API_ENDPOINTS } from '../config/api';

export const notesService = {
  /**
   * GET /api/v1/users/me/notes — all of the caller's notes. `linkType`
   * ('NONE' | 'TRANSACTION' | 'PROPERTY' | 'DOCUMENT' | 'PARTY' | 'CONVERSATION')
   * and `hasReminder` (boolean) are optional filters for the My Notes filter bar.
   */
  listNotes: async (firebaseToken, { linkType, hasReminder } = {}) => {
    const params = {
      ...(linkType && { linkType }),
      ...(hasReminder !== undefined && hasReminder !== null && { hasReminder }),
    };
    const response = await apiClient.get(API_ENDPOINTS.notes, {
      headers: { Authorization: `Bearer ${firebaseToken}` },
      params,
    });
    return response.data || [];
  },

  /**
   * GET /api/v1/users/me/notes/by-link — the caller's notes attached to one
   * specific entity. Powers the pen-icon count badge and inline notes list.
   *
   * `transactionId` should always be passed when the entity belongs to a
   * transaction (milestone/stage, document, party) — the backend uses it to
   * scope the lookup to this transaction, since `linkId` alone isn't
   * guaranteed unique across transactions for TRANSACTION-linked notes (e.g.
   * prospect-stage and active-listing-stage ids are drawn from a shared
   * template/vocabulary, not generated per transaction).
   */
  listNotesByLink: async (firebaseToken, linkType, linkId, transactionId) => {
    const response = await apiClient.get(API_ENDPOINTS.notesByLink, {
      headers: { Authorization: `Bearer ${firebaseToken}` },
      params: { linkType, linkId, ...(transactionId ? { transactionId } : {}) },
    });
    return response.data || [];
  },

  /**
   * POST /api/v1/users/me/notes. `link` is optional — omit entirely (or pass
   * undefined) for an untagged note; when present it must carry
   * { linkType, linkId, linkLabel } (linkLabel is a denormalized display-label
   * snapshot captured client-side, e.g. "214 Maple Ct — Inspection Contingency" —
   * the backend has no synchronous access to resolve one itself).
   *
   * `transactionId`/`transactionName` are optional and only meaningful when this
   * note is created from within a transaction's Timeline/Parties/Documents
   * tab — there `link.linkId` points at a milestone/participant/document, not
   * the transaction itself, so the transaction has to be captured separately
   * to make "jump to this transaction" possible later.
   */
  createNote: async (firebaseToken, { text, link, reminderDate, transactionId, transactionName }) => {
    const body = {
      text,
      ...(link ? { linkType: link.linkType, linkId: link.linkId, linkLabel: link.linkLabel } : {}),
      ...(reminderDate ? { reminderDate } : {}),
      ...(transactionId ? { transactionId } : {}),
      ...(transactionName ? { transactionName } : {}),
    };
    const response = await apiClient.post(API_ENDPOINTS.notes, body, {
      headers: { Authorization: `Bearer ${firebaseToken}` },
    });
    return response.data;
  },

  /**
   * PUT /api/v1/users/me/notes/{id} — updates text and/or reminderDate only;
   * a note's link is set once at creation and is not editable.
   */
  updateNote: async (firebaseToken, noteId, { text, reminderDate }) => {
    const response = await apiClient.put(
      API_ENDPOINTS.noteById(noteId),
      { text, reminderDate: reminderDate || null },
      { headers: { Authorization: `Bearer ${firebaseToken}` } },
    );
    return response.data;
  },

  deleteNote: async (firebaseToken, noteId) => {
    await apiClient.delete(API_ENDPOINTS.noteById(noteId), {
      headers: { Authorization: `Bearer ${firebaseToken}` },
    });
  },

  /**
   * PATCH /api/v1/users/me/notes/{id}/transaction-name — self-heal only,
   * used by MyNotesPage to refresh a note's transactionName snapshot when it
   * was captured as the literal fallback string "Transaction" (see
   * TransactionTimeline.js/TransactionDocuments.js/TransactionChatParties.js's
   * NotesPanel wiring) instead of a real transaction name/address. Does not
   * touch the note's link (type/id/label), which stays immutable.
   */
  repairTransactionName: async (firebaseToken, noteId, transactionName) => {
    const response = await apiClient.patch(
      API_ENDPOINTS.noteTransactionNameRepair(noteId),
      { transactionName },
      { headers: { Authorization: `Bearer ${firebaseToken}` } },
    );
    return response.data;
  },
};
